/**
 * The preferences that must be right before the first paint (PLT-03,
 * profile template): appearance and formatting. They live on the person's
 * row and arrive with /v1/me from PR 5; until then the shell keeps them in
 * localStorage under `lms.*` (ADR-0008), which is also what
 * public/theme.js reads before the app loads so there is no flash.
 *
 * Nothing here is personal data.
 */
export type Theme = "system" | "light" | "dark";
export type Motion = "auto" | "standard" | "reduced";
export type TextSize = "default" | "large" | "x-large";
export type Contrast = "auto" | "default" | "more";
export type DateFormat = "auto" | "iso" | "us" | "eu";

export interface Preferences {
  theme: Theme;
  motion: Motion;
  textSize: TextSize;
  contrast: Contrast;
  /** `auto` follows the device; otherwise an IANA zone. */
  timeZone: string;
  dateFormat: DateFormat;
}

export const DEFAULT_PREFERENCES: Preferences = {
  theme: "system",
  motion: "auto",
  textSize: "default",
  contrast: "auto",
  timeZone: "auto",
  dateFormat: "auto",
};

const KEYS = {
  theme: "lms.theme",
  motion: "lms.motion",
  textSize: "lms.textSize",
  contrast: "lms.contrast",
  timeZone: "lms.timeZone",
  dateFormat: "lms.dateFormat",
} as const;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode or a full store: the first paint just follows the OS.
  }
}

const oneOf = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
  value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;

/** What the browser remembers, before /v1/me has answered. */
export function cachedPreferences(): Preferences {
  return {
    theme: oneOf(read(KEYS.theme), ["system", "light", "dark"], "system"),
    motion: oneOf(read(KEYS.motion), ["auto", "standard", "reduced"], "auto"),
    textSize: oneOf(read(KEYS.textSize), ["default", "large", "x-large"], "default"),
    contrast: oneOf(read(KEYS.contrast), ["auto", "default", "more"], "auto"),
    timeZone: read(KEYS.timeZone) ?? "auto",
    dateFormat: oneOf(read(KEYS.dateFormat), ["auto", "iso", "us", "eu"], "auto"),
  };
}

export function cachePreferences(p: Preferences): void {
  for (const [k, key] of Object.entries(KEYS) as Array<[keyof Preferences, string]>) write(key, String(p[k]));
}

const media = (query: string): boolean => typeof window !== "undefined" && !!window.matchMedia?.(query).matches;

export function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return media("(prefers-color-scheme: dark)") ? "dark" : "light";
}

export function resolveMotion(motion: Motion): "standard" | "reduced" {
  if (motion !== "auto") return motion;
  return media("(prefers-reduced-motion: reduce)") ? "reduced" : "standard";
}

export function resolveContrast(contrast: Contrast): "default" | "more" {
  if (contrast !== "auto") return contrast;
  return media("(prefers-contrast: more)") ? "more" : "default";
}

/**
 * Writes the resolved appearance onto <html> — the same attributes
 * public/theme.js set before the app loaded — and caches the preference.
 * Returns a disposer that stops following the OS.
 */
export function applyAppearance(p: Pick<Preferences, "theme" | "motion" | "textSize" | "contrast">): () => void {
  const root = document.documentElement;
  const paint = () => {
    root.dataset["theme"] = resolveTheme(p.theme);
    root.dataset["motion"] = resolveMotion(p.motion);
    root.dataset["contrast"] = resolveContrast(p.contrast);
    root.dataset["textSize"] = p.textSize;
  };
  paint();
  write(KEYS.theme, p.theme);
  write(KEYS.motion, p.motion);
  write(KEYS.textSize, p.textSize);
  write(KEYS.contrast, p.contrast);
  const queries = ["(prefers-color-scheme: dark)", "(prefers-reduced-motion: reduce)", "(prefers-contrast: more)"]
    .map((q) => window.matchMedia?.(q))
    .filter((q): q is MediaQueryList => !!q);
  for (const q of queries) q.addEventListener("change", paint);
  return () => {
    for (const q of queries) q.removeEventListener("change", paint);
  };
}

export function detectedTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function effectiveTimeZone(p: Pick<Preferences, "timeZone">): string {
  return p.timeZone === "auto" ? detectedTimeZone() : p.timeZone;
}

const pad = (n: number) => String(n).padStart(2, "0");

function parts(iso: string, timeZone: string): Record<string, string> {
  const f = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
  const out: Record<string, string> = {};
  for (const x of f.formatToParts(new Date(iso))) out[x.type] = x.value;
  if (out["hour"] === "24") out["hour"] = "00"; // some engines print midnight as 24
  return out;
}

/** A timestamp in the person's zone and format; `withTime` false gives the date alone. */
export function formatWhen(iso: string, p: Pick<Preferences, "timeZone" | "dateFormat">, withTime = true): string {
  const timeZone = effectiveTimeZone(p);
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (p.dateFormat === "auto") {
    return withTime ? d.toLocaleString(undefined, { timeZone }) : d.toLocaleDateString(undefined, { timeZone });
  }
  const x = parts(iso, timeZone);
  const date = p.dateFormat === "iso" ? `${x["year"]}-${x["month"]}-${x["day"]}` : p.dateFormat === "us" ? `${x["month"]}/${x["day"]}/${x["year"]}` : `${x["day"]}/${x["month"]}/${x["year"]}`;
  if (!withTime) return date;
  if (p.dateFormat === "us") {
    const h = Number(x["hour"]);
    return `${date}, ${((h + 11) % 12) + 1}:${x["minute"]} ${h < 12 ? "AM" : "PM"}`;
  }
  return `${date} ${pad(Number(x["hour"]))}:${x["minute"]}`;
}
